/* eslint-disable @typescript-eslint/no-explicit-any */
import { DATE_FORMATS } from "@dw/constants";
import { dayjs, Dayjs } from '@dw/dayjs';

import { isDate, isTimeOnly, isDateOnly } from "./dateUtils";

const parseTimeOnlyString = (timeOnlyString: string): Dayjs => {
  const today = dayjs();
  const timeParsed = dayjs(timeOnlyString, DATE_FORMATS.HourMinuteSecond24Hour);
  return today
    .set('hour', timeParsed.hour())
    .set('minute', timeParsed.minute())
    .set('second', timeParsed.second());
}

const parseDateOnlyString = (dateOnlyString: string): Dayjs => dayjs(dateOnlyString, DATE_FORMATS.YearMonthDayIso).startOf('day');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const cleanJsonResponse = (response: any): any => {
  if (!response) {
    return response;
  }

  if (Array.isArray(response)) {
    for (let i = 0; i < response.length; i++) {
      response[i] = cleanJsonResponse(response[i]);
    }
  } else if (typeof response === "object") {
    for (const key in response) {
      if (Array.isArray(response[key])) {
        for (let i = 0; i < response[key].length; i++) {
          response[key][i] = cleanJsonResponse(response[key][i]);
        }
      } else if (typeof response[key] === "object") {
        response[key] = cleanJsonResponse(response[key]);
      } else if (isDate(response[key])) {
        response[key] = dayjs(response[key]);
      } else if (isTimeOnly(response[key])) {
        response[key] = parseTimeOnlyString(response[key])
      } else if (isDateOnly(response[key])) {
        response[key] = parseDateOnlyString(response[key]);
      }
    }
  } else if (isDate(response)) {
    return dayjs(response);
  } else if (isTimeOnly(response)) {
    return parseTimeOnlyString(response);
  } else if (isDateOnly(response)) {
    return parseDateOnlyString(response);
  }

  return response;
};

export function searchItems<T>(searchText: string, items: T[], itemSearchKeys: (keyof T)[]): T[] {
  if (!searchText) {
    return items;
  }

  const searchTextParts = searchText.split(' ').map(x => x.trim().toLowerCase()).filter(x => x.length > 0);

  return items
    .filter(x => {
      let hasMatch = false;
      for (const key of itemSearchKeys) {
        const searchPropValueParts = `${x[key]}`.toLowerCase().split(' ').map(x => x.trim().toLowerCase()).filter(x => x.length > 0);
        hasMatch = searchPropValueParts.filter(y => searchTextParts.filter(z => y.includes(z)).length > 0).length > 0;
        if (hasMatch) {
          break;
        }
      }

      return hasMatch;
    });
}

export function convertToDictionary<T>(items: T[], keyAttribute: (keyof T | ((item: T) => string))): Record<string, T> {
  return (items ?? []).reduce((acc, item) => {
    const key = typeof keyAttribute === 'function' ? keyAttribute(item) : String(item[keyAttribute]);
    acc[key] = item;
    return acc;
  }, {} as Record<string, T>);
}

export const convertToSerializable = (obj: any, seen = new WeakSet()): any => {
  if (obj && typeof obj === 'object') {
    if (seen.has(obj)) {
      return '[Circular]';
    }
    seen.add(obj);

    if (obj instanceof Error) {
      const serialized: Record<string, any> = {
        name: obj.name,
        message: obj.message,
        stack: obj.stack,
      };

      for (const key of Object.getOwnPropertyNames(obj)) {
        if (!(key in serialized)) {
          try {
            serialized[key] = convertToSerializable((obj as any)[key], seen);
          } catch {
            serialized[key] = '[Unserializable]';
          }
        }
      }

      return serialized;
    }

    // DOM Events or other system-native objects
    if (typeof Event !== 'undefined' && obj instanceof Event) {
      if ('reason' in obj) {
        return convertToSerializable(obj.reason);
      }

      return {
        type: obj.type,
        target: obj.target ? '[DOMElement]' : null,
        currentTarget: obj.currentTarget ? '[DOMElement]' : null,
        defaultPrevented: obj.defaultPrevented,
        isTrusted: obj.isTrusted,
        timeStamp: obj.timeStamp
      };
    }

    if (Array.isArray(obj)) {
      return obj.map(item => convertToSerializable(item, seen));
    }

    const serializableObject: any = {};
    for (const key in obj) {
      try {
        serializableObject[key] = convertToSerializable(obj[key], seen);
      } catch {
        serializableObject[key] = '[Unserializable]';
      }
    }
    return serializableObject;
  }

  return obj;
};
